// Tab "Maschine" (M1-M15 of the inventory, APP-PARITY.md): temperature with setpoint and heating,
// pills, notes with their buttons, the running shot like the round display, the last shot with taste, care bars, the
// last 20 minutes. Also used by the tab "Bezug" (running shot, rinse, taste).
import { tasteTagCatalog, type BeanItem, type ShotItem } from '@brewcore/app-contract';
import type { BeansView } from '../beans.js';
import type { HistorySample, HistoryView } from '../history.js';
import type { CommandInput } from '../commands.js';
import type { LiveShotView } from '../live-shot.js';
import { ratioOf, stopModeOf } from '../recipe.js';
import { dayText, localDayOf, nextSwitchOn, parsePlan } from '../schedule.js';
import type { ShotsView } from '../shots.js';
import { hasData, LIVE_QUIET_MS, liveLook, localTime, presenceOf, type LiveLook } from '../calm.js';
import { calmAgoText } from '../i18n.js';
import {
  careOf,
  duration,
  heatingText,
  liveOf,
  machineNotes,
  opOf,
  wifiQuality,
  type Note,
  type Tone,
} from '../status.js';
import { actionView, textOf, type ActionView, type ScreenContext } from './context.js';
import { backflushTimes } from './device.js';
import { curveData, liveCurveData, type CurveData } from './curves.js';
import { shotChart, type ShotChartView } from './chart.js';
import { liveLanes, type LaneChartView } from './lanes.js';
import { scheduleSummary } from './settings.js';
import { compareTarget } from './ready.js';
import {
  deviation,
  formatAuto,
  formatNumber,
  metaOf,
  offTarget,
  shotMs,
  shotTime,
  signed,
  whenText,
  type ShotMeta,
} from './format.js';

// ------------------------------------------------------------------------------------------------ temperature

export interface TemperatureView {
  temp: number | null;
  tempText: string;
  /** "Soll 92,0 °C" while the controller aims elsewhere than the setpoint (standby warm, profile), else null */
  aim: string | null;
  setpoint: number;
  heater: { fraction: number; text: string };
  /** "an · Standby in 12 min", "Standby · warm 70 °C, aus in 2 h", "aus" */
  heating: string;
  pidOn: boolean;
  pidEditable: boolean;
}

export function temperatureView(ctx: ScreenContext): TemperatureView {
  const live = liveOf(ctx.view, ctx.now);
  const temp = typeof live?.currentTemp === 'number' ? live.currentTemp : null;
  const target = live?.targetTemp;
  const setpoint = ctx.num('brew.setpoint');
  const power = typeof live?.heaterPower === 'number' ? live.heaterPower : null;
  const heat = heatingText(ctx.view, ctx.value('pid.enabled') === true, ctx.num('standby.warm_temp'));
  return {
    temp,
    tempText: temp !== null ? formatNumber(temp, 1, ctx.lang) : '–',
    aim:
      typeof target === 'number' && target > 0 && Math.abs(target - setpoint) > 0.05
        ? `${ctx.tr('setpointShort')} ${formatNumber(target, 1, ctx.lang)} °C`
        : null,
    setpoint,
    heater: {
      fraction: Math.max(0, Math.min(1, (power ?? 0) / 100)),
      text: power !== null ? `${Math.round(power)} %` : '–',
    },
    heating: textOf(ctx, heat.key, heat.vars),
    pidOn: ctx.value('pid.enabled') === true,
    pidEditable: ctx.canEdit('pid.enabled'),
  };
}

/**
 * The heating as a plain status row of the machine tab (Dominik, 19.10.2026: no "Heizen" switch on the home screen,
 * standby turns the heater off; PID on/off lives under Einstellungen → Fortgeschritten): heater power right, below it
 * what comes next ("Standby in 24 min", "Standby · warm 70 °C, aus in 2 h") or that the controller is off.
 */
export function heatingRow(ctx: ScreenContext): { label: string; power: string; hint: string | null } {
  const t = temperatureView(ctx);
  const op = opOf(ctx.view);
  let hint: string | null;
  if (op.state === 80) hint = t.heating;
  else if (!t.pidOn) hint = ctx.tr('heatOffHome');
  else {
    const inMin = op.standby?.inMin;
    const parts = [
      typeof inMin === 'number' ? ctx.tr('standbyIn', { t: duration(inMin) }) : null,
      t.aim,
    ].filter((x): x is string => x !== null);
    hint = parts.length ? parts.join(' · ') : null;
  }
  return { label: ctx.tr('heater'), power: t.heater.text, hint };
}

// ------------------------------------------------------------------------------------------------ ring

export interface GaugeReading {
  /** live: current; aging: the last value after a short gap, dimmed with its age; none: no value (core/calm.ts) */
  look: LiveLook;
  /** the temperature on the ring and in its centre (current or held), undefined without one */
  temp: number | undefined;
  /** the ring's scale: the controller's target; in standby (target 0) the recipe's setpoint */
  target: number | undefined;
  /** the machine's state: the held live value unless op is newer (core/status.ts liveOf), else op */
  state: number | undefined;
  standby: boolean;
  /** the line under the temperature: "Soll 93,0 °C", "Stand vor 20 s" or "Keine Live-Werte" (null before data) */
  sub: string | null;
}

/**
 * The temperature ring of the machine tab. A short gap keeps the layout and the last value (dimmed, with its age in
 * 5-s steps); only a gap of 60 s or a machine that is offline turns it into "Keine Live-Werte" (core/calm.ts).
 */
export function gaugeReading(ctx: ScreenContext, machineSetpoint?: number): GaugeReading {
  const { look, ageMs } = liveLook(ctx.view, ctx.now);
  const live = liveOf(ctx.view, ctx.now);
  const opState = opOf(ctx.view).state;
  const temp = look !== 'none' && typeof live?.currentTemp === 'number' ? live.currentTemp : undefined;
  const state = live?.state ?? opState;
  const standby = state === 80 || opState === 80;
  // the machine's setpoint, not the one being stepped: the ring keeps its scale while −/+ collects taps
  const target = compareTarget(ctx, live, standby, machineSetpoint);
  const sub =
    look === 'aging' && ageMs !== null
      ? ctx.tr('liveAsOf', { ago: calmAgoText(ctx.lang, ageMs) })
      : look === 'live'
        ? target !== undefined
          ? `${ctx.tr('target')} ${formatNumber(target, 1, ctx.lang)}\u00a0°C`
          : null
        : hasData(ctx.view)
          ? ctx.tr('noLive')
          : null;
  return { look, temp, target, state, standby, sub };
}

// ------------------------------------------------------------------------------------------------ pills

export interface PillView {
  key: 'scale' | 'holiday' | 'next' | 'wifi';
  text: string;
  tone: Tone;
}

/**
 * Scale and battery, holiday, next switch-on, WLAN (paintPills() of the local app). Scale and WLAN only while the
 * machine is reachable (online, or a drop that is still short): an offline machine has neither "WLAN gut" nor a scale.
 */
export function pillViews(ctx: ScreenContext): PillView[] {
  const out: PillView[] = [];
  const live = liveOf(ctx.view, ctx.now);
  const presence = presenceOf(ctx.view, ctx.now);
  const reachable = presence === 'online' || presence === 'reconnecting';
  const scale = ctx.view.groups.scale?.data as { status?: string; battery?: number | null } | undefined;
  if (reachable && ctx.scale && scale && (scale.status === 'connected' || scale.status === 'disconnected')) {
    const on = scale.status === 'connected';
    const battery = live?.battery ?? scale.battery ?? null;
    out.push({
      key: 'scale',
      text: on ? `${ctx.tr('scale')}${battery !== null ? ` · ${battery} %` : ''}` : ctx.tr('scaleGone'),
      tone: on ? (battery !== null && battery <= 20 ? 'heat' : 'ready') : 'off',
    });
  }
  const enabled = ctx.value('schedule.enabled') === true;
  const pause = ctx.num('schedule.pause_until');
  if (enabled && pause >= ctx.today)
    out.push({ key: 'holiday', text: ctx.tr('holidayPill', { d: dayText(pause, ctx.lang) }), tone: 'calm' });
  const next = nextSwitchOn(parsePlan(ctx.value('schedule.plan')), {
    enabled,
    pauseUntil: pause,
    now: ctx.now,
    zone: ctx.zone,
  });
  if (next !== null) {
    const days = localDayOf(next, ctx.zone) - ctx.today;
    const loc = ctx.lang === 'de' ? 'de-DE' : 'en-GB';
    const d = new Date(next);
    const day =
      days === 0
        ? ctx.tr('today')
        : days === 1
          ? ctx.tr('tomorrow')
          : d.toLocaleDateString(loc, { weekday: 'short', timeZone: ctx.zone });
    const time = d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit', timeZone: ctx.zone });
    out.push({ key: 'next', text: ctx.tr('schedNext', { d: day, t: time }), tone: 'heat' });
  }
  const health = ctx.view.groups.health?.data as { rssi?: number } | undefined;
  const q = wifiQuality(live?.rssi ?? health?.rssi);
  if (q && reachable)
    out.push({
      key: 'wifi',
      text: `${ctx.tr('wifi')} ${ctx.tr(q)}`,
      tone: q === 'wifiGood' ? 'ready' : q === 'wifiOk' ? 'heat' : 'error',
    });
  return out;
}

// ------------------------------------------------------------------------------------------------ notes

export interface NoteView {
  key: string;
  text: string;
  tone: Note['tone'];
  action: ActionView | null;
  /** a note about an amount (the drip tray): a short title, the amount, how full (0..1) */
  amount?: { title: string; text: string; fraction: number };
}

/** "Jetzt spülen": one flush pulse, only from ready with the switch off and the water level sensor on. */
export function rinseAction(ctx: ScreenContext): ActionView & { rinsing: boolean } {
  const live = liveOf(ctx.view, ctx.now);
  const op = opOf(ctx.view);
  const state = live?.state ?? op.state;
  const rinsing = state === 25 && (live?.pulses ?? op.warmup?.pulses) === 1;
  const sw = live?.sw ?? op.switch ?? false;
  const a = actionView(ctx, 'flush.start', {
    label: rinsing ? 'rinsing' : 'flushNow',
    args: { pulses: 1 },
    when: ctx.watertank && state === 10 && !sw && !rinsing,
    ask: { title: 'flushConfirmTitle', text: 'flushConfirm', ok: 'flushGo' },
  });
  return { ...a, rinsing };
}

/** A note with its button (rinse, drip tray emptied, next step of the descaling programme). */
export function noteView(ctx: ScreenContext, note: Note): NoteView {
  let action: ActionView | null = null;
  if (note.action === 'rinse') action = rinseAction(ctx);
  else if (note.action === 'drip') action = actionView(ctx, 'drip.emptied', { label: 'dripEmptied' });
  else if (note.action === 'descaleNext') {
    const phase = liveOf(ctx.view, ctx.now)?.dsc ?? opOf(ctx.view).descale?.phase ?? 0;
    action = actionView(ctx, 'descale.next', { label: 'dsNext', expect: { 'descale.phase': phase } });
  }
  const view: NoteView = { key: note.key, text: textOf(ctx, note.key, note.vars), tone: note.tone, action };
  const cap = ctx.num('drip.capacity');
  if (note.key === 'dripDue' && cap > 0 && typeof note.vars?.ml === 'number')
    view.amount = {
      title: ctx.tr('dripDueTitle'),
      text: ctx.tr('dripNow', { ml: note.vars.ml, cap }),
      fraction: Math.min(1, note.vars.ml / cap),
    };
  return view;
}

export function machineNoteViews(ctx: ScreenContext): NoteView[] {
  return machineNotes(ctx.view, ctx.now, {
    remindAfter: ctx.num('backflush.remind_after'),
    dripCapacity: ctx.num('drip.capacity'),
    watertank: ctx.watertank,
    backflushTimes: backflushTimes(ctx),
  }).map((n) => noteView(ctx, n));
}

// ------------------------------------------------------------------------------------------------ running shot

export interface LiveShotScreen {
  phase: 'pi' | 'brew' | 'done';
  /** PRE-INFUSION / BEZUG / FERTIG */
  label: string;
  seconds: number;
  secondsText: string;
  /** "+6 s" after the pre-infusion */
  piText: string | null;
  weight: number | null;
  weightText: string | null;
  goal: { value: number; unit: 'g' | 's' } | null;
  /** "Ziel 36 g · 2,1 g/s", "Waage nicht verbunden – Ziel 25 s" or "Stopp von Hand" */
  goalText: string;
  /** after a shot by weight: "Pumpe aus bei 34,1 g" and the deviation (green up to 1 g, D-M02) */
  result: { stop: string; deviation: string; off: boolean } | null;
  /** 0..1 (more past the target) for the ring */
  progress: number;
  /** "Schalter wieder auf AUS" a minute after a held shot */
  switchBack: string | null;
  curve: CurveData | null;
  reference: CurveData | null;
  targetWeight: number | null;
  /** seconds of pre-infusion (band in the curve) */
  preinfusion: number;
  minSeconds: number;
  /** the chart as for a stored shot (scales, grid, target, pre-infusion, the reference behind it) */
  chart: ShotChartView | null;
  /** the big number (prototype "Bezug läuft"): whole seconds while it runs */
  heroText: string;
  /** under it: "21,0 g" and "von 36 g" (or "von 25 s"); after the shot "in 29 s" and the ratio */
  amount: { now: string | null; of: string | null };
  /** the steps under the ring: pre-infusion (when on), brewing, done; `fraction` how far each is */
  steps: { key: 'pi' | 'brew' | 'done'; label: string; fraction: number; current: boolean }[];
  /** the brew temperature over the chart */
  tempText: string | null;
  /** the running shot in the prototype's lanes: weight and flow, the target */
  lanes: LaneChartView | null;
  /** after the shot: "in 29 s" and "1:2,0" */
  done: { time: string; ratio: string | null } | null;
  /** while it runs: when its last value came (this device's clock) and the flow then, for liveEstimate */
  sampledAt: number | null;
  flowRate: number | null;
  /** the scale is switched on but was missing in this shot: the display writes "Waage getrennt" */
  scaleLost: boolean;
}

/** How far the ring and the seconds may run on past the last value of a running shot. */
export const LIVE_RUN_ON_S = 1.5;

/**
 * The ring and the seconds between two live values (Dominik 21.10.2026: the ring was "komplett laggy"): the values come
 * about once a second and sometimes later, so the time runs on with this device's clock and, by weight, the weight
 * with the last flow, at most LIVE_RUN_ON_S past the last value. In the pre-infusion and after the shot as reported.
 */
export function liveEstimate(screen: LiveShotScreen, now: number): { seconds: number; progress: number } {
  if (screen.phase === 'done' || screen.sampledAt === null)
    return { seconds: screen.seconds, progress: screen.progress };
  const extra = Math.min(LIVE_RUN_ON_S, Math.max(0, (now - screen.sampledAt) / 1000));
  const goal = screen.goal;
  const ahead =
    !goal || goal.value <= 0
      ? 0
      : goal.unit === 's'
        ? screen.phase === 'pi'
          ? 0
          : extra / goal.value
        : (Math.max(0, screen.flowRate ?? 0) * extra) / goal.value;
  return { seconds: screen.seconds + extra, progress: screen.progress + ahead };
}

/**
 * A running shot whose live values stopped: the machine is offline or restarting, or for more than a short gap
 * (15 s, core/calm.ts) no live value came while the newer op group says the shot is over. The ring then goes back to
 * the temperature instead of a shot frozen mid-way. A short gap keeps the shot (no flip at its normal end).
 */
function shotLost(ctx: ScreenContext): boolean {
  const presence = presenceOf(ctx.view, ctx.now);
  if (presence === 'offline' || presence === 'restarting' || presence === 'unknown') return true;
  const live = ctx.view.live;
  const op = ctx.view.groups.op;
  if (!live || !op) return false;
  const silent = ctx.now - localTime(ctx.view, live.receivedAt) > LIVE_QUIET_MS;
  return silent && op.receivedAt > live.receivedAt && (op.data as { state?: number }).state !== 20;
}

/** How long the result stays after the switch is back: the machine's "FERTIG" time, at least 5 s (default 10 s). */
export const postBrewMs = (ctx: ScreenContext): number =>
  Math.max(5, ctx.num('display.post_brew_timer_duration', 10)) * 1000;

/**
 * A shot that op reports before its first live value (op comes first, the live values once a second): it starts at
 * 0 s now instead of a second later (22.10.2026: the ring said "Bezug 0.0 s" over the temperature for a second).
 */
function startingShot(ctx: ScreenContext, view: LiveShotView): LiveShotView {
  if (view.brewing) return view;
  const op = ctx.view.groups.op;
  const live = ctx.view.live;
  const d = op?.data as { state?: number; rinse?: boolean } | undefined;
  if (!op || d?.state !== 20 || d.rinse === true || (live && live.receivedAt >= op.receivedAt)) return view;
  const scale = (live?.data as { scale?: number } | undefined)?.scale === 2;
  return {
    brewing: true,
    shot: {
      points: [],
      time: 0,
      weight: scale ? 0 : null,
      flow: null,
      pi: ctx.value('brew.pre_infusion.enabled') === true ? 1 : 0,
      doneAt: null,
      held: false,
      noScale: !scale,
      sampledAt: localTime(ctx.view, op.receivedAt),
    },
  };
}

/**
 * Not a shot for the machine (ShotLog::record(), lib/Orione OrioneShots.h): under 5 s, with the scale under 5 g,
 * without it under 10 s. It was a rinse with the portafilter out or a slip of the switch: no result, no "FERTIG".
 */
export function notAShot(shot: NonNullable<LiveShotView['shot']>): boolean {
  const grams = shot.noScale ? null : (shot.stopWeight ?? shot.weight);
  return shot.time < 5 || (grams !== null ? grams < 5 : shot.time < 10);
}

/** The running shot like the round display (paintShotLive() of the local app); null when no shot is shown. */
export function liveShotScreen(
  ctx: ScreenContext,
  tracked: LiveShotView,
  reference: ShotItem | null,
): LiveShotScreen | null {
  const view = startingShot(ctx, tracked);
  const x = view.shot;
  if (!x) return null;
  if (!view.brewing && notAShot(x)) return null;
  if (view.brewing && shotLost(ctx)) return null;
  // the rinse after the shot has begun (op says so before its first live value): the result goes now, as the round
  // display's cancel() (activityRing shows the rinse)
  const now = liveOf(ctx.view, ctx.now);
  if (
    !view.brewing &&
    (now?.state ?? opOf(ctx.view).state) === 20 &&
    (now?.rinse ?? opOf(ctx.view).rinse) === true
  )
    return null;
  // the tank ran empty: its screen comes first, as on the display (selectScreen: WaterTankEmpty before the shot)
  if ((now?.state ?? opOf(ctx.view).state) === 70) return null;
  // with the switch back the result goes as the round display's "FERTIG" does (Dominik 21.10.2026: the screen did not
  // go away); while the switch still holds the shot it stays
  if (!view.brewing && !x.held && x.doneAt !== null && ctx.now - x.doneAt > postBrewMs(ctx)) return null;
  const mode = stopModeOf(ctx.value, ctx.scale);
  const pi =
    ctx.value('brew.pre_infusion.enabled') === true
      ? ctx.num('brew.pre_infusion.time') + ctx.num('brew.pre_infusion.pause')
      : 0;
  const inPi = view.brewing && x.pi > 0;
  const seconds = inPi || !pi ? x.time : Math.max(0, x.time - pi);
  const goalW = ctx.num('brew.by_weight.target_weight');
  const goalT = ctx.num('brew.by_time.target_time');
  // without the scale at any point of the shot the firmware stops it by time (shotMode() of the local app, D-M03)
  const fallback = mode === 'weight' && (x.noScale === true || x.weight === null);
  const byWeight = mode === 'weight' && !fallback;
  const goal = byWeight
    ? { value: goalW, unit: 'g' as const }
    : mode === 'time' || fallback
      ? { value: goalT, unit: 's' as const }
      : null;
  const reached = byWeight ? (x.weight ?? 0) : inPi ? 0 : seconds;
  const flow = view.brewing && x.flow !== null ? ` · ${formatNumber(x.flow, 1, ctx.lang)} g/s` : '';
  const stopWeight = x.stopWeight ?? null;
  const off = byWeight && x.weight !== null ? Math.round((x.weight - goalW) * 10) / 10 : null;
  const liveCurve = liveCurveData(x.points);
  const refCurve = reference ? curveData(reference.curve) : null;
  return {
    phase: !view.brewing ? 'done' : inPi ? 'pi' : 'brew',
    label: ctx.tr(!view.brewing ? 'dialDone' : inPi ? 'dialPi' : 'dialBrew'),
    seconds,
    secondsText: formatNumber(seconds, 1, ctx.lang),
    piText: pi && !inPi ? `+${formatNumber(pi, 0, ctx.lang)} s` : null,
    weight: x.weight,
    weightText: x.weight !== null ? `${formatNumber(x.weight, 1, ctx.lang)} g` : null,
    goal: goal && goal.value > 0 ? goal : null,
    goalText:
      (goal && goal.value > 0
        ? fallback
          ? ctx.tr('noScaleGoal', { n: formatAuto(goal.value, ctx.lang) })
          : `${ctx.tr('goal')} ${formatAuto(goal.value, ctx.lang)} ${goal.unit}`
        : ctx.tr('stopHandShort')) + flow,
    result:
      !view.brewing && byWeight && goalW > 0 && stopWeight !== null && off !== null
        ? {
            stop: `${ctx.tr('atStop')} ${formatNumber(stopWeight, 1, ctx.lang)} g`,
            deviation: `${signed(off, ctx.lang)} g`,
            off: Math.abs(off) > 1,
          }
        : null,
    progress: goal && goal.value > 0 ? Math.max(0, reached / goal.value) : 0,
    switchBack:
      !view.brewing && x.held && x.doneAt !== null && ctx.now - x.doneAt > 60_000
        ? ctx.tr('switchBack')
        : null,
    curve: liveCurve,
    reference: refCurve,
    targetWeight: byWeight ? goalW : null,
    preinfusion: pi,
    minSeconds: mode === 'time' ? goalT + pi : 30,
    heroText: formatNumber(Math.floor(seconds), 0, ctx.lang),
    amount: {
      now: x.weight !== null ? `${formatNumber(x.weight, 1, ctx.lang)} g` : null,
      of:
        goal && goal.value > 0
          ? ctx.tr(goal.unit === 'g' ? 'ofGrams' : 'ofSeconds', { n: formatAuto(goal.value, ctx.lang) })
          : null,
    },
    steps: [
      ...(pi
        ? [
            {
              key: 'pi' as const,
              label: ctx.tr('sPreinf'),
              fraction: inPi ? Math.min(1, x.time / pi) : 1,
              current: inPi,
            },
          ]
        : []),
      {
        key: 'brew' as const,
        label: ctx.tr('phaseBrew'),
        fraction: !view.brewing
          ? 1
          : inPi
            ? 0
            : goal && goal.value > 0
              ? Math.min(1, reached / goal.value)
              : 0.5,
        current: view.brewing && !inPi,
      },
      {
        key: 'done' as const,
        label: ctx.tr('phaseDone'),
        fraction: view.brewing ? 0 : 1,
        current: !view.brewing,
      },
    ],
    tempText:
      typeof liveOf(ctx.view, ctx.now)?.currentTemp === 'number'
        ? formatNumber(liveOf(ctx.view, ctx.now)?.currentTemp ?? 0, 1, ctx.lang)
        : null,
    lanes: liveCurve
      ? liveLanes(ctx, liveCurve, {
          targetWeight: byWeight ? goalW : null,
          spanS: mode === 'time' ? goalT + pi : 30,
        })
      : null,
    sampledAt: view.brewing ? (x.sampledAt ?? null) : null,
    flowRate: view.brewing ? x.flow : null,
    scaleLost: ctx.scale && (x.noScale === true || x.weight === null),
    done: !view.brewing
      ? {
          // the time after the pre-infusion, as the running ring counts it and the round display's FERTIG shows it
          // (Dominik 15.10.2026: "eigentlich interessiert ja nur die durchlaufzeit")
          time: ctx.tr('shotDoneIn', { s: formatNumber(Math.round(seconds), 0, ctx.lang) }),
          ratio:
            x.weight !== null && ctx.num('brew.dose') > 0
              ? `1:${formatNumber(ratioOf(ctx.num('brew.dose'), x.weight) ?? 0, 1, ctx.lang)}`
              : null,
        }
      : null,
    chart: liveCurve
      ? shotChart(ctx, liveCurve, refCurve, {
          targetWeight: byWeight ? goalW : null,
          pi: pi
            ? { burst: ctx.num('brew.pre_infusion.time'), pause: ctx.num('brew.pre_infusion.pause') }
            : null,
          spanS: mode === 'time' ? goalT + pi : 30,
          otherLabel: refCurve ? ctx.tr('refChip') : null,
        })
      : null,
  };
}

// ------------------------------------------------------------------------------------------------ last shot, taste

export interface TasteView {
  question: string;
  options: { value: 1 | 2 | 3; label: string; on: boolean }[];
  /** taste tags of the catalog (concept beans-and-shot-history §3.1), several at once */
  tagsLabel: string;
  /** auto: detected by the machine (channeling), shown as such */
  tags: { value: string; label: string; on: boolean; hint?: string; auto: boolean }[];
}

/** The text of a taste tag in the app's language (fixed IDs: both languages show the same data). */
export function tasteTagText(ctx: ScreenContext, id: string): string {
  const t = tasteTagCatalog.tags.find((x) => x.id === id);
  return t ? (ctx.lang === 'de' ? t.de : t.en) : id;
}

export function tasteView(ctx: ScreenContext, shot: ShotItem): TasteView {
  const on = new Set(shot.tags ?? []);
  const auto = new Set(shot.tagsAuto ?? []);
  return {
    question: ctx.tr('tasteQ'),
    options: ([1, 2, 3] as const).map((value) => ({
      value,
      label: ctx.tr(value === 1 ? 'sour' : value === 2 ? 'good' : 'bitter'),
      on: shot.rating === value,
    })),
    tagsLabel: ctx.tr('tasteTags'),
    tags: tasteTagCatalog.tags.map((t) => ({
      value: t.id,
      label: auto.has(t.id) ? `${tasteTagText(ctx, t.id)} · ${ctx.tr('tagAuto')}` : tasteTagText(ctx, t.id),
      on: on.has(t.id),
      ...(t.hint ? { hint: ctx.lang === 'de' ? t.hint.de : t.hint.en } : {}),
      auto: auto.has(t.id),
    })),
  };
}

/** A tapped tag goes on or off; the list stays in the catalog's order. */
export function tasteTagsAfterTap(current: readonly string[] | undefined, tapped: string): string[] {
  const now = new Set(current ?? []);
  if (now.has(tapped)) now.delete(tapped);
  else now.add(tapped);
  return tasteTagCatalog.tags.map((t) => t.id).filter((id) => now.has(id));
}

/**
 * The reference a running shot is compared with: the current bean's (reference per bean, 19.10.2026), loaded on
 * demand; without a current bean in the library (or an API before slice 5) the machine's one of the period.
 */
export function liveReference(sv: ShotsView, bv?: BeansView): { id: string | null; shot: ShotItem | null } {
  const current: BeanItem | undefined = bv?.items.find((b) => b.current);
  if (!current || !('referenceShotId' in current))
    return { id: sv.reference.shotId, shot: sv.reference.shot };
  const id = current.referenceShotId ?? null;
  const shot = id ? (sv.curves[id] ?? (id === sv.reference.shotId ? sv.reference.shot : null)) : null;
  return { id, shot };
}

/** A tap on the chosen taste takes it back (null), any other sets it. */
export const tasteAfterTap = (current: number | null, tapped: 1 | 2 | 3): 1 | 2 | 3 | null =>
  current === tapped ? null : tapped;

export interface ShotFigures {
  id: string;
  when: string;
  time: string;
  piText: string | null;
  weightText: string | null;
  ratioText: string | null;
  deviation: { text: string; off: boolean } | null;
}

export function shotFigures(ctx: ScreenContext, shot: ShotItem): ShotFigures {
  const m = metaOf(shot);
  const t = shotTime(m);
  const dev = deviation(m);
  const ratio = m.dose && typeof m.weight === 'number' ? ratioOf(m.dose, m.weight) : null;
  return {
    id: shot.id,
    when: whenText(shotMs(shot), ctx.lang, ctx.now),
    time: `${formatNumber(t.main, 1, ctx.lang)} s`,
    piText: t.pi ? `+${formatNumber(t.pi, 0, ctx.lang)} s` : null,
    weightText: typeof m.weight === 'number' ? `${formatNumber(m.weight, 1, ctx.lang)} g` : null,
    ratioText: ratio ? `1:${formatNumber(ratio, 1, ctx.lang)}` : null,
    deviation: dev ? { text: `${signed(dev.value, ctx.lang)} ${dev.unit}`, off: offTarget(dev) } : null,
  };
}

export interface LastShotCard {
  title: string;
  shot: (ShotFigures & { channeling: string | null; taste: TasteView }) | null;
  /** "3 heute · 14 diese Woche" */
  counters: string | null;
}

/** Card "Letzter Bezug"; null without shots and counters. */
export function lastShotCard(ctx: ScreenContext, shots: ShotsView): LastShotCard | null {
  const care = careOf(ctx.view);
  const shot = shots.items[0];
  if (!shot && !care) return null;
  return {
    title: ctx.tr('lastShot'),
    shot: shot
      ? {
          ...shotFigures(ctx, shot),
          channeling: metaOf(shot).channeling ? ctx.tr('chNote') : null,
          taste: tasteView(ctx, shot),
        }
      : null,
    counters: care ? `${ctx.tr('cTodayN', { n: care.today })} · ${ctx.tr('cWeekN', { n: care.week })}` : null,
  };
}

// ------------------------------------------------------------------------------------------------ care, history

export interface CareBar {
  key: 'backflush' | 'descale' | 'drip';
  label: string;
  fraction: number;
  text: string;
  due: boolean;
}

/** Card "Pflege": backflush, descaling, drip tray, each only where a limit is set. */
export function careBars(ctx: ScreenContext): CareBar[] {
  const care = careOf(ctx.view);
  if (!care) return [];
  const every = ctx.num('backflush.remind_after');
  const litres = ctx.num('descale.litres');
  const cap = ctx.num('drip.capacity');
  const out: CareBar[] = [];
  if (every > 0)
    out.push({
      key: 'backflush',
      label: ctx.tr('careBf'),
      fraction: care.shotsSinceBackflush / every,
      text: ctx.tr('careLeftShots', { n: Math.max(0, every - care.shotsSinceBackflush) }),
      due: care.due.backflush,
    });
  if (litres > 0)
    out.push({
      key: 'descale',
      label: ctx.tr('careDescale'),
      fraction: care.waterMl / (litres * 1000),
      text: ctx.tr('careLeftL', { l: formatNumber(Math.max(0, litres - care.waterMl / 1000), 0, ctx.lang) }),
      due: care.due.descale,
    });
  if (cap > 0)
    out.push({
      key: 'drip',
      label: ctx.tr('careDrip'),
      fraction: care.dripMl / cap,
      text: `${care.dripMl} / ${cap} ml`,
      due: care.dripMl >= cap * 0.8,
    });
  return out;
}

export interface HistoryCard {
  title: string;
  samples: readonly HistorySample[];
  /** the window of the chart: from 20 minutes ago to now */
  from: number;
  to: number;
  /** text instead of the chart while there are fewer than two samples */
  empty: string | null;
  legend: { temp: string; target: string; power: string };
}

export function historyCard(ctx: ScreenContext, h: HistoryView): HistoryCard {
  return {
    title: ctx.tr('history'),
    samples: h.samples,
    from: ctx.now - 20 * 60_000,
    to: ctx.now,
    empty: h.samples.length > 1 ? null : ctx.tr(h.emptyAfterRestart ? 'historyEmpty' : 'historyWaiting'),
    legend: { temp: ctx.tr('tempLine'), target: ctx.tr('setLine'), power: ctx.tr('heatLine') },
  };
}

// ------------------------------------------------------------------------------------------------ prototype (21.10.2026)

/**
 * About how many minutes until the setpoint, from the warming of the last two minutes (prototype "bereit in etwa
 * 4 min"): a straight line through the samples, only while it clearly rises; null otherwise or beyond 30 minutes.
 */
export function readyEtaMin(
  samples: readonly HistorySample[],
  temp: number | undefined,
  target: number | undefined,
  now: number,
): number | null {
  if (temp === undefined || target === undefined || temp >= target - 1) return null;
  const recent = samples.filter(
    (s): s is HistorySample & { temp: number } =>
      s.t >= now - 120_000 && typeof s.temp === 'number' && s.temp > 0,
  );
  if (recent.length < 8) return null;
  const first = recent[0] as HistorySample;
  const last = recent[recent.length - 1] as HistorySample;
  if (last.t - first.t < 30_000) return null;
  const mt = recent.reduce((a, s) => a + s.t, 0) / recent.length;
  const mv = recent.reduce((a, s) => a + s.temp, 0) / recent.length;
  let num = 0;
  let den = 0;
  for (const s of recent) {
    num += (s.t - mt) * (s.temp - mv);
    den += (s.t - mt) ** 2;
  }
  const perMs = den > 0 ? num / den : 0;
  // below 0.6 K a minute it is not heating up (holding, or a sensor that drifts)
  if (perMs * 60_000 < 0.6) return null;
  const min = Math.ceil((target - 0.5 - temp) / perMs / 60_000);
  return min > 30 ? null : Math.max(1, min);
}

const clockText = (ctx: ScreenContext, ms: number): string =>
  new Date(ms).toLocaleTimeString(ctx.lang === 'de' ? 'de-DE' : 'en-GB', {
    hour: '2-digit',
    minute: '2-digit',
    timeZone: ctx.zone,
  });

/**
 * The line under the temperature (prototype boards "Maschine: …"): while it heats up when it will be ready, when ready
 * when it goes into standby, in standby when the schedule has it ready (its switch-on plus the learned heat-up,
 * core/schedule.ts; or until when it is paused). The setpoint when nothing else is known.
 */
export function ringLine(
  ctx: ScreenContext,
  reading: GaugeReading,
  tone: 'heat' | 'ready' | 'cool' | 'idle',
  history: readonly HistorySample[],
): string | null {
  if (reading.look !== 'live') return reading.sub;
  const op = opOf(ctx.view);
  const setpointText =
    reading.target !== undefined
      ? `${ctx.tr('target')} ${formatNumber(reading.target, 1, ctx.lang)}\u00a0°C`
      : null;
  if (reading.standby) {
    const warm = op.standby?.warmMin;
    if (typeof warm === 'number' && warm > 0) {
      const h = heatingText(ctx.view, true, ctx.num('standby.warm_temp'));
      return textOf(ctx, h.key, h.vars);
    }
    const enabled = ctx.value('schedule.enabled') === true;
    const pause = ctx.num('schedule.pause_until');
    if (enabled && pause >= ctx.today) return ctx.tr('pausedUntil', { d: dayText(pause, ctx.lang) });
    const next = nextSwitchOn(parsePlan(ctx.value('schedule.plan')), {
      enabled,
      pauseUntil: pause,
      now: ctx.now,
      zone: ctx.zone,
    });
    if (next === null) return ctx.tr('heaterOff');
    const ready = next + ctx.lead * 60_000;
    const days = localDayOf(ready, ctx.zone) - ctx.today;
    const t = clockText(ctx, ready);
    if (days === 0) return ctx.tr('readyAtTime', { t });
    const d =
      days === 1
        ? ctx.tr('tomorrow')
        : new Date(ready).toLocaleDateString(ctx.lang === 'de' ? 'de-DE' : 'en-GB', {
            weekday: 'short',
            timeZone: ctx.zone,
          });
    return ctx.tr('readyDayAt', { d, t });
  }
  if (ctx.value('pid.enabled') === false) return ctx.tr('heatOffHome');
  if (tone === 'heat') {
    const eta = readyEtaMin(history, reading.temp, reading.target, ctx.now);
    return eta !== null ? ctx.tr('readyIn', { n: eta }) : setpointText;
  }
  if (tone === 'ready') {
    const inMin = op.standby?.inMin;
    if (typeof inMin === 'number' && inMin >= 0)
      return ctx.tr('standbyAt', { t: clockText(ctx, ctx.now + inMin * 60_000) });
  }
  return setpointText;
}

/** The setpoint over the ring while it heats up (prototype "Maschine: heizt auf": "93°"), else null. */
export function ringTop(ctx: ScreenContext, reading: GaugeReading, tone: string): string | null {
  return tone === 'heat' && reading.look === 'live' && reading.target !== undefined && !reading.standby
    ? `${formatAuto(Math.round(reading.target * 10) / 10, ctx.lang)}°`
    : null;
}

/** Shots of the machine's day (the local day of the API's list). */
const todayShots = (ctx: ScreenContext, shots: ShotsView): ShotItem[] =>
  shots.items.filter((s) => localDayOf(shotMs(s), ctx.zone) === ctx.today);

/** In standby the day so far (prototype "Maschine: Standby"): shots, how many tasted good, the average time. */
export function todayCard(
  ctx: ScreenContext,
  shots: ShotsView,
): { title: string; facts: { key: string; label: string; value: string }[] } {
  const care = careOf(ctx.view);
  const day = todayShots(ctx, shots);
  const times = day.map((s) => shotTime(metaOf(s)).main).filter((t) => t > 0);
  const avg = times.length ? times.reduce((a, b) => a + b, 0) / times.length : null;
  return {
    title: ctx.tr('cToday'),
    facts: [
      { key: 'shots', label: ctx.tr('shotsLabel'), value: String(care?.today ?? day.length) },
      { key: 'good', label: ctx.tr('good'), value: String(day.filter((s) => s.rating === 2).length) },
      // the mean only once there is one (WRITING.md 0a: no "–" for what is not known)
      ...(avg !== null
        ? [{ key: 'time', label: ctx.tr('avgTime'), value: `${formatNumber(avg, 0, ctx.lang)}\u00a0s` }]
        : []),
    ],
  };
}

/** The schedule as a row of the standby screen: "Mo–Fr 06:45 · Sa, So 08:30", "pausiert bis …", "aus". */
export const scheduleRow = (ctx: ScreenContext): { label: string; hint: string } => ({
  label: ctx.tr('scheduleRow'),
  hint: scheduleSummary(ctx),
});

/** What a shot was made with: dose, grind, temperature and its course, the stop with its target, pre-infusion; null without a grind. */
export function recipeKey(m: ShotMeta): string | null {
  if (!m.grind) return null;
  const target = m.stopMode === 'weight' ? m.targetWeight : m.stopMode === 'time' ? m.targetTime : null;
  const pi = m.preinfusion ? `${m.preinfusion.burst}+${m.preinfusion.pause}` : 'off';
  return [m.dose ?? '', m.grind, m.setpoint ?? '', m.tempEnd ?? 0, m.stopMode ?? '', target ?? '', pi].join(
    '|',
  );
}

/**
 * The usual range: the good shots (rated "passt") of the same bean before this one with exactly its recipe, at most
 * five (Dominik 25.10.2026: 25 s counted "wie die letzten fünf", whose range held a coarser 23.5 s shot and a 34.5 s one;
 * "ja, 3 so umsetzen").
 */
export function usualShots(shot: ShotItem, items: readonly ShotItem[]): ShotItem[] {
  const key = recipeKey(metaOf(shot));
  if (key === null) return [];
  const at = shotMs(shot);
  const bean = shot.beanId ?? metaOf(shot).bean?.name ?? null;
  return items
    .filter((s) => s.id !== shot.id && shotMs(s) < at)
    .filter((s) => (s.beanId ?? metaOf(s).bean?.name ?? null) === bean)
    .filter((s) => s.rating === 2 && recipeKey(metaOf(s)) === key)
    .slice(0, 5);
}

export interface UsualCheck {
  /** within the range of the usual shots (1 s and 1 g of slack) */
  ok: boolean;
  /** how many shots the range is made of */
  k: number;
  /** seconds longer (+) or shorter (−) than the range, 0 inside */
  dt: number;
  /** grams more (+) or less (−) than the range, 0 inside */
  dw: number;
}

/** Time and weight of a shot against its usual shots (usualShots); null with fewer than two of them. */
export function usualCheck(shot: ShotItem, items: readonly ShotItem[]): UsualCheck | null {
  const others = usualShots(shot, items);
  const m = metaOf(shot);
  const times = others.map((s) => shotTime(metaOf(s)).main).filter((t) => t > 0);
  const weights = others.map((s) => metaOf(s).weight).filter((w): w is number => typeof w === 'number');
  if (times.length < 2) return null;
  const out = (v: number, xs: number[], slack: number) => {
    if (xs.length < 2) return 0;
    const lo = Math.min(...xs) - slack;
    const hi = Math.max(...xs) + slack;
    return v > hi ? v - hi : v < lo ? v - lo : 0;
  };
  const dt = Math.round(out(shotTime(m).main, times, 1));
  const dw = typeof m.weight === 'number' ? Math.round(out(m.weight, weights, 1) * 10) / 10 : 0;
  return { ok: dt === 0 && dw === 0, k: times.length, dt, dw };
}

/** "wie üblich" or how it differs (prototype "Letzter Bezug": "28 s  36,8 g  wie üblich"). */
export function usualText(ctx: ScreenContext, u: UsualCheck | null): { text: string; off: boolean } | null {
  if (!u) return null;
  if (u.ok) return { text: ctx.tr('asUsual'), off: false };
  if (u.dt !== 0)
    return { text: ctx.tr(u.dt > 0 ? 'usualSlower' : 'usualFaster', { n: Math.abs(u.dt) }), off: true };
  return {
    text: ctx.tr(u.dw > 0 ? 'usualMore' : 'usualLess', { n: formatNumber(Math.abs(u.dw), 1, ctx.lang) }),
    off: true,
  };
}

/** The app's reading of a shot against the usual range (prototype "Ein Bezug", a line with a check or a pulse). */
export function usualNotes(ctx: ScreenContext, u: UsualCheck | null): { ok: boolean; text: string }[] {
  if (!u) return [];
  const k = u.k >= 2 && u.k <= 5 ? ctx.tr(`n${u.k}` as 'n2') : String(u.k);
  if (u.ok) return [{ ok: true, text: ctx.tr('usualNoteOk', { k }) }];
  const out: { ok: boolean; text: string }[] = [];
  if (u.dt !== 0)
    out.push({
      ok: false,
      text: ctx.tr(u.dt > 0 ? 'usualNoteLonger' : 'usualNoteShorter', { n: Math.abs(u.dt), k }),
    });
  if (u.dw !== 0)
    out.push({
      ok: false,
      text: ctx.tr(u.dw > 0 ? 'usualNoteMore' : 'usualNoteLess', {
        n: formatNumber(Math.abs(u.dw), 1, ctx.lang),
        k,
      }),
    });
  return out;
}

export interface GrindTip {
  text: string;
  /** the grind size it suggests, as the recipe writes it */
  to: string | null;
  apply: CommandInput | null;
  applyLabel: string | null;
  doneText: string | null;
}

/**
 * What to change after a rating (prototype "Bezug fertig"): sour, finer; bitter, coarser; good, keep it. With a
 * numeric grind size and a known scale direction the next step and a button for it (one decimal: 0.2, whole numbers:
 * 1); otherwise the advice without a number.
 */
export function grindTip(ctx: ScreenContext, rating: 1 | 2 | 3): GrindTip {
  if (rating === 2)
    return { text: ctx.tr('tipGood'), to: null, apply: null, applyLabel: null, doneText: null };
  const finer = rating === 1;
  const raw = String(ctx.value('brew.grind') ?? '').trim();
  const scale = ctx.num('brew.grind_scale');
  const num = Number(raw.replace(',', '.'));
  const plain = {
    text: ctx.tr(finer ? 'tipSourPlain' : 'tipBitterPlain'),
    to: null,
    apply: null,
    applyLabel: null,
    doneText: null,
  };
  if (!raw || !/^\d+([.,]\d+)?$/.test(raw) || !Number.isFinite(num) || (scale !== 1 && scale !== 2))
    return plain;
  const decimals = (raw.split(/[.,]/)[1] ?? '').length;
  const step = decimals > 0 ? 2 / 10 ** decimals : 1;
  const up = (scale === 1) === finer;
  const next = Math.round((num + (up ? step : -step)) * 10 ** decimals) / 10 ** decimals;
  if (next <= 0) return plain;
  const to = next.toFixed(decimals).replace('.', raw.includes(',') ? ',' : '.');
  const shown = (v: string) => (ctx.lang === 'de' ? v.replace('.', ',') : v.replace(',', '.'));
  return {
    text: ctx.tr(finer ? 'tipSour' : 'tipBitter', { to: shown(to), from: shown(raw) }),
    to,
    apply: ctx.canEdit('brew.grind') ? { kind: 'set', params: { 'brew.grind': to } } : null,
    applyLabel: ctx.tr('tipApply', { to: shown(to) }),
    doneText: ctx.tr('tipDone', { to: shown(to) }),
  };
}

/** The stored shot of the one that just finished (it arrives a moment later), for its rating; null before. */
export function justBrewed(ctx: ScreenContext, shots: ShotsView): ShotItem | null {
  const s = shots.items[0];
  return s && ctx.now - shotMs(s) < 10 * 60_000 ? s : null;
}
